import React, { useState, useEffect } from 'react';
import { Lock, Mail, Settings, Database, Send, Eye, EyeOff, FileEdit, Cpu, Rocket } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import ContentEditor from './ContentEditor';
import AISettings from './AISettings';
import TranslationsPanel from './TranslationsPanel';
import PilotRequests from './PilotRequests';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LANG_NAMES_DE = { en: 'Englisch', fr: 'Französisch', it: 'Italienisch', es: 'Spanisch' };

const AdminDashboard = () => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [password, setPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [activeTab, setActiveTab] = useState('submissions');
  
  // Submissions
  const [submissions, setSubmissions] = useState([]);
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [showOriginal, setShowOriginal] = useState(false);
  
  // Email Settings
  const [emailSettings, setEmailSettings] = useState({
    gmail_address: '',
    gmail_app_password: '',
    notification_email: '',
    enabled: false
  });
  const [showPassword, setShowPassword] = useState(false);
  const [emailMessage, setEmailMessage] = useState('');

  const authHeaders = () => ({
    Authorization: `Bearer ${localStorage.getItem('admin_token') || ''}`
  });

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    setIsLoggedIn(false);
    setPassword('');
    setSubmissions([]);
    setSelectedSubmission(null);
  };

  const handleAuthError = (error) => {
    if (error.response?.status === 401) {
      handleLogout();
      return true;
    }
    return false;
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError('');
    
    try {
      const response = await axios.post(`${API}/admin/login`, { password });
      
      if (response.data.success && response.data.token) {
        localStorage.setItem('admin_token', response.data.token);
        setIsLoggedIn(true);
        loadSubmissions();
        loadEmailSettings();
      } else {
        setLoginError('Falsches Passwort');
      }
    } catch (error) {
      if (error.response?.status === 429) {
        setLoginError('Zu viele Fehlversuche. Bitte in 15 Minuten erneut versuchen.');
      } else if (error.response?.status === 401) {
        setLoginError('Falsches Passwort');
      } else {
        setLoginError('Login fehlgeschlagen');
      }
    }
  };

  const loadSubmissions = async () => {
    try {
      const response = await axios.get(`${API}/ai-contact/submissions`, { headers: authHeaders() });
      setSubmissions(response.data);
    } catch (error) {
      if (!handleAuthError(error)) console.error('Error loading submissions:', error);
    }
  };

  const loadEmailSettings = async () => {
    try {
      const response = await axios.get(`${API}/admin/email-settings`, { headers: authHeaders() });
      setEmailSettings({
        ...response.data,
        gmail_app_password: '' // Don't show the actual password
      });
    } catch (error) {
      if (!handleAuthError(error)) console.error('Error loading email settings:', error);
    }
  };

  const handleSaveEmailSettings = async (e) => {
    e.preventDefault();
    setEmailMessage('');
    
    try {
      await axios.post(`${API}/admin/email-settings`, emailSettings, { headers: authHeaders() });
      setEmailMessage('✅ Einstellungen gespeichert');
      setTimeout(() => setEmailMessage(''), 3000);
    } catch (error) {
      if (!handleAuthError(error)) setEmailMessage('❌ Fehler beim Speichern');
    }
  };

  const handleTestEmail = async () => {
    setEmailMessage('');
    
    try {
      const response = await axios.post(`${API}/admin/test-email`, null, { headers: authHeaders() });
      if (response.data.success) {
        setEmailMessage('✅ Test-E-Mail gesendet!');
      } else {
        setEmailMessage(`❌ ${response.data.message}`);
      }
    } catch (error) {
      if (!handleAuthError(error)) setEmailMessage('❌ Fehler beim Senden');
    }
  };

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (token && token !== 'authenticated' && token !== 'admin_authenticated') {
      setIsLoggedIn(true);
      loadSubmissions();
      loadEmailSettings();
    } else if (token) {
      localStorage.removeItem('admin_token');
    }
  }, []);

  if (!isLoggedIn) {
    return (
      <div className="min-h-screen bg-[#F5F5F7] flex items-center justify-center px-6">
        <div className="bg-white rounded-3xl p-12 shadow-lg max-w-md w-full">
          <div className="flex justify-center mb-8">
            <div className="w-16 h-16 bg-[#0071E3]/10 rounded-2xl flex items-center justify-center">
              <Lock className="w-8 h-8 text-[#0071E3]" strokeWidth={1.5} />
            </div>
          </div>
          
          <h1 className="text-3xl font-semibold text-[#1D1D1F] mb-2 text-center">
            Admin Login
          </h1>
          <p className="text-[#86868B] text-center mb-8">
            InvictaFlow Dashboard
          </p>
          
          <form onSubmit={handleLogin} className="space-y-6">
            <div>
              <Input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Passwort"
                className="bg-[#F5F5F7] border-0 text-[#1D1D1F] rounded-xl h-12 focus:ring-1 focus:ring-[#0071E3]"
              />
            </div>
            
            {loginError && (
              <p className="text-red-500 text-sm text-center">{loginError}</p>
            )}
            
            <Button
              type="submit"
              className="w-full bg-[#0071E3] hover:bg-[#0077ED] text-white rounded-full h-12"
            >
              Anmelden
            </Button>
            
            <p className="text-xs text-[#86868B] text-center">
              Zugang nur für Administratoren der InvictaX Group GmbH
            </p>
          </form>

          <p className="text-xs text-[#86868B] text-center mt-8 pt-6 border-t border-[#D2D2D7]" data-testid="admin-login-invictax-notice">
            Powered by InvictaX Group GmbH
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F5F5F7] py-8 px-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-semibold text-[#1D1D1F] mb-2">
            Admin Dashboard
          </h1>
          <div className="flex items-center justify-between">
            <p className="text-[#86868B]">InvictaFlow Verwaltung</p>
            <Button
              variant="outline"
              onClick={handleLogout}
              className="rounded-full border-[#D2D2D7] text-[#1D1D1F] hover:bg-[#F5F5F7] h-9 px-5 text-sm"
              data-testid="admin-logout"
            >
              Abmelden
            </Button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex space-x-4 mb-8 border-b border-[#D2D2D7]">
          <button
            onClick={() => setActiveTab('submissions')}
            className={`pb-3 px-4 font-medium transition-colors ${
              activeTab === 'submissions'
                ? 'text-[#0071E3] border-b-2 border-[#0071E3]'
                : 'text-[#86868B] hover:text-[#1D1D1F]'
            }`}
            data-testid="admin-tab-submissions"
          >
            <Database className="w-4 h-4 inline mr-2" />
            Anfragen ({submissions.length})
          </button>
          <button
            onClick={() => setActiveTab('pilots')}
            className={`pb-3 px-4 font-medium transition-colors ${
              activeTab === 'pilots'
                ? 'text-[#0071E3] border-b-2 border-[#0071E3]'
                : 'text-[#86868B] hover:text-[#1D1D1F]'
            }`}
            data-testid="admin-tab-pilots"
          >
            <Rocket className="w-4 h-4 inline mr-2" />
            Pilot-Anfragen
          </button>
          <button
            onClick={() => setActiveTab('content')}
            className={`pb-3 px-4 font-medium transition-colors ${
              activeTab === 'content'
                ? 'text-[#0071E3] border-b-2 border-[#0071E3]'
                : 'text-[#86868B] hover:text-[#1D1D1F]'
            }`}
            data-testid="admin-tab-content"
          >
            <FileEdit className="w-4 h-4 inline mr-2" />
            Inhalte
          </button>
          <button
            onClick={() => setActiveTab('ai')}
            className={`pb-3 px-4 font-medium transition-colors ${
              activeTab === 'ai'
                ? 'text-[#0071E3] border-b-2 border-[#0071E3]'
                : 'text-[#86868B] hover:text-[#1D1D1F]'
            }`}
            data-testid="admin-tab-ai"
          >
            <Cpu className="w-4 h-4 inline mr-2" />
            KI-Einstellungen
          </button>
          <button
            onClick={() => setActiveTab('settings')}
            className={`pb-3 px-4 font-medium transition-colors ${
              activeTab === 'settings'
                ? 'text-[#0071E3] border-b-2 border-[#0071E3]'
                : 'text-[#86868B] hover:text-[#1D1D1F]'
            }`}
            data-testid="admin-tab-settings"
          >
            <Settings className="w-4 h-4 inline mr-2" />
            E-Mail Einstellungen
          </button>
          <button
            onClick={() => setActiveTab('password')}
            className={`pb-3 px-4 font-medium transition-colors ${
              activeTab === 'password'
                ? 'text-[#0071E3] border-b-2 border-[#0071E3]'
                : 'text-[#86868B] hover:text-[#1D1D1F]'
            }`}
            data-testid="admin-tab-password"
          >
            <Lock className="w-4 h-4 inline mr-2" />
            Passwort ändern
          </button>
        </div>

        {/* Content */}
        {activeTab === 'content' && (
          <>
            <TranslationsPanel authHeaders={authHeaders} onAuthError={handleAuthError} />
            <ContentEditor />
          </>
        )}

        {activeTab === 'ai' && <AISettings authHeaders={authHeaders} onAuthError={handleAuthError} />}

        {activeTab === 'pilots' && <PilotRequests authHeaders={authHeaders} onAuthError={handleAuthError} />}

        {activeTab === 'submissions' && (
          <div className="space-y-4">
            {submissions.length === 0 ? (
              <div className="bg-white rounded-2xl p-12 text-center">
                <p className="text-[#86868B]">Noch keine Anfragen vorhanden</p>
              </div>
            ) : (
              submissions.map((submission) => (
                <div
                  key={submission.id}
                  className="bg-white rounded-2xl p-6 cursor-pointer hover:shadow-lg transition-shadow"
                  onClick={() => { setSelectedSubmission(submission); setShowOriginal(false); }}
                >
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <p className="text-sm text-[#86868B]">
                        {new Date(submission.timestamp).toLocaleString('de-CH')}
                      </p>
                    </div>
                    {submission.language && submission.language !== 'de' && (
                      <span
                        className="text-xs bg-[#0071E3]/10 text-[#0071E3] px-2.5 py-1 rounded-full font-semibold uppercase"
                        data-testid={`submission-lang-badge-${submission.id}`}
                      >
                        {submission.language}
                      </span>
                    )}
                  </div>
                  <div className="bg-[#F5F5F7] rounded-xl p-4">
                    <pre className="text-sm text-[#1D1D1F] whitespace-pre-wrap">
                      {submission.summary}
                    </pre>
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {activeTab === 'password' && (
          <div className="bg-white rounded-2xl p-8 max-w-2xl">
            <h2 className="text-2xl font-semibold text-[#1D1D1F] mb-6">
              Passwort ändern
            </h2>
            
            <form className="space-y-6" onSubmit={async (e) => {
              e.preventDefault();
              const formData = new FormData(e.target);
              const oldPassword = formData.get('oldPassword');
              const newPassword = formData.get('newPassword');
              const confirmPassword = formData.get('confirmPassword');
              
              if (newPassword !== confirmPassword) {
                setEmailMessage('❌ Passwörter stimmen nicht überein');
                return;
              }
              
              if (newPassword.length < 8) {
                setEmailMessage('❌ Passwort muss mindestens 8 Zeichen lang sein');
                return;
              }
              
              try {
                const response = await axios.post(`${API}/admin/change-password`, {
                  old_password: oldPassword,
                  new_password: newPassword
                }, { headers: authHeaders() });
                
                if (response.data.success) {
                  setEmailMessage('✅ Passwort erfolgreich geändert');
                  e.target.reset();
                } else {
                  setEmailMessage(`❌ ${response.data.message}`);
                }
              } catch (error) {
                const detail = error.response?.data?.detail;
                setEmailMessage(`❌ ${typeof detail === 'string' ? detail : 'Fehler beim Ändern des Passworts'}`);
              }
            }}>
              <div>
                <label className="block text-sm font-medium text-[#1D1D1F] mb-2">
                  Altes Passwort
                </label>
                <Input
                  type="password"
                  name="oldPassword"
                  required
                  className="bg-[#F5F5F7] border-0 text-[#1D1D1F] rounded-xl h-12"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-[#1D1D1F] mb-2">
                  Neues Passwort
                </label>
                <Input
                  type="password"
                  name="newPassword"
                  required
                  minLength={8}
                  className="bg-[#F5F5F7] border-0 text-[#1D1D1F] rounded-xl h-12"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-[#1D1D1F] mb-2">
                  Passwort bestätigen
                </label>
                <Input
                  type="password"
                  name="confirmPassword"
                  required
                  minLength={8}
                  className="bg-[#F5F5F7] border-0 text-[#1D1D1F] rounded-xl h-12"
                />
              </div>

              {emailMessage && (
                <div className="bg-[#F5F5F7] rounded-xl p-4">
                  <p className="text-[#1D1D1F]">{emailMessage}</p>
                </div>
              )}

              <Button
                type="submit"
                className="w-full bg-[#0071E3] hover:bg-[#0077ED] text-white rounded-full h-12"
              >
                Passwort ändern
              </Button>
            </form>
          </div>
        )}

        {activeTab === 'settings' && (
          <div className="bg-white rounded-2xl p-8">
            <h2 className="text-2xl font-semibold text-[#1D1D1F] mb-6">
              E-Mail Benachrichtigungen
            </h2>
            
            <form onSubmit={handleSaveEmailSettings} className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-[#1D1D1F] mb-2">
                  Gmail-Adresse (Absender)
                </label>
                <Input
                  type="email"
                  value={emailSettings.gmail_address}
                  onChange={(e) => setEmailSettings({...emailSettings, gmail_address: e.target.value})}
                  placeholder="ihre@gmail.com"
                  className="bg-[#F5F5F7] border-0 text-[#1D1D1F] rounded-xl h-12"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-[#1D1D1F] mb-2">
                  Gmail App-Passwort
                </label>
                <div className="relative">
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    value={emailSettings.gmail_app_password}
                    onChange={(e) => setEmailSettings({...emailSettings, gmail_app_password: e.target.value})}
                    placeholder="xxxx xxxx xxxx xxxx"
                    className="bg-[#F5F5F7] border-0 text-[#1D1D1F] rounded-xl h-12 pr-12"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[#86868B]"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
                <p className="text-xs text-[#86868B] mt-2">
                  Erstellen Sie ein App-Passwort unter: https://myaccount.google.com/apppasswords
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-[#1D1D1F] mb-2">
                  Empfänger E-Mail (Benachrichtigungen an)
                </label>
                <Input
                  type="email"
                  value={emailSettings.notification_email}
                  onChange={(e) => setEmailSettings({...emailSettings, notification_email: e.target.value})}
                  placeholder="info@invictaflow.ch"
                  className="bg-[#F5F5F7] border-0 text-[#1D1D1F] rounded-xl h-12"
                />
              </div>

              <div className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  checked={emailSettings.enabled}
                  onChange={(e) => setEmailSettings({...emailSettings, enabled: e.target.checked})}
                  className="w-5 h-5 text-[#0071E3]"
                />
                <label className="text-[#1D1D1F]">
                  E-Mail-Benachrichtigungen aktivieren
                </label>
              </div>

              {emailMessage && (
                <div className="bg-[#F5F5F7] rounded-xl p-4">
                  <p className="text-[#1D1D1F]">{emailMessage}</p>
                </div>
              )}

              <div className="flex space-x-4">
                <Button
                  type="submit"
                  className="flex-1 bg-[#0071E3] hover:bg-[#0077ED] text-white rounded-full h-12"
                >
                  Einstellungen speichern
                </Button>
                <Button
                  type="button"
                  onClick={handleTestEmail}
                  variant="outline"
                  className="border-[#0071E3] text-[#0071E3] hover:bg-[#0071E3] hover:text-white rounded-full h-12 px-8"
                >
                  <Send className="w-4 h-4 mr-2" />
                  Test-E-Mail
                </Button>
              </div>
            </form>
          </div>
        )}
      </div>

      <p className="text-xs text-[#86868B] text-center mt-12" data-testid="admin-dashboard-invictax-notice">
        Powered by InvictaX Group GmbH
      </p>

      {/* Submission Detail Modal */}
      {selectedSubmission && (
        <div
          className="fixed inset-0 bg-[#1D1D1F]/40 backdrop-blur-md z-50 flex items-center justify-center p-6"
          onClick={() => setSelectedSubmission(null)}
        >
          <div
            className="bg-white rounded-3xl max-w-3xl w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-8">
              <h2 className="text-2xl font-semibold text-[#1D1D1F] mb-4">
                Projektanfrage Details
              </h2>
              <p className="text-sm text-[#86868B] mb-2">
                {new Date(selectedSubmission.timestamp).toLocaleString('de-CH')}
              </p>
              <p className="text-xs text-[#86868B] mb-6" data-testid="submission-consent-status">
                AGB & Datenschutz akzeptiert: {selectedSubmission.terms_accepted ? '✅ Ja' : '– keine Angabe (ältere Anfrage)'}
              </p>

              {selectedSubmission.language && selectedSubmission.language !== 'de' && (
                <div className="flex flex-wrap items-center gap-3 mb-6" data-testid="submission-language-info">
                  <span className="text-xs bg-amber-100 text-amber-700 px-3 py-1.5 rounded-full font-medium">
                    {selectedSubmission.conversation_history_de
                      ? `Übersetzt aus ${LANG_NAMES_DE[selectedSubmission.language] || selectedSubmission.language.toUpperCase()}`
                      : `Anfrage auf ${LANG_NAMES_DE[selectedSubmission.language] || selectedSubmission.language.toUpperCase()} (nicht übersetzt)`}
                  </span>
                  {selectedSubmission.conversation_history_de && (
                    <button
                      onClick={() => setShowOriginal(!showOriginal)}
                      className="text-xs text-[#0071E3] hover:underline font-medium"
                      data-testid="submission-toggle-original"
                    >
                      {showOriginal ? 'Deutsche Übersetzung anzeigen' : 'Original anzeigen'}
                    </button>
                  )}
                </div>
              )}

              {selectedSubmission.company_info && (selectedSubmission.company_info.zefix || selectedSubmission.company_info.company_name || selectedSubmission.company_info.analysis) && (
                <div className="bg-[#0071E3]/5 rounded-xl p-4 mb-6" data-testid="submission-company-info">
                  <p className="text-xs text-[#0071E3] font-semibold mb-2">Firmen-Informationen</p>
                  {selectedSubmission.company_info.zefix ? (
                    <>
                      <p className="text-[#1D1D1F] font-medium">
                        {selectedSubmission.company_info.zefix.name}
                        {selectedSubmission.company_info.zefix.legal_form && ` (${selectedSubmission.company_info.zefix.legal_form})`}
                      </p>
                      <p className="text-sm text-[#86868B] mt-1">
                        Sitz: {selectedSubmission.company_info.zefix.legal_seat} · UID: {selectedSubmission.company_info.zefix.uid} · Status: {selectedSubmission.company_info.zefix.status}
                      </p>
                      {selectedSubmission.company_info.zefix.excerpt_url && (
                        <a
                          href={selectedSubmission.company_info.zefix.excerpt_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm text-[#0071E3] hover:underline"
                          data-testid="submission-zefix-link"
                        >
                          Handelsregister-Auszug öffnen →
                        </a>
                      )}
                    </>
                  ) : (
                    selectedSubmission.company_info.company_name && (
                      <p className="text-[#1D1D1F] font-medium">
                        {selectedSubmission.company_info.company_name} <span className="text-[#86868B] font-normal">(nicht im Zefix gefunden)</span>
                      </p>
                    )
                  )}
                  {selectedSubmission.company_info.website_url && (
                    <p className="text-sm text-[#86868B] mt-1">
                      Webseite:{' '}
                      <a
                        href={selectedSubmission.company_info.website_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-[#0071E3] hover:underline"
                      >
                        {selectedSubmission.company_info.website_url}
                      </a>
                    </p>
                  )}
                  {selectedSubmission.company_info.analysis && (
                    <div className="mt-3 pt-3 border-t border-[#0071E3]/10">
                      <p className="text-xs text-[#86868B] mb-1">KI-Analyse</p>
                      <p className="text-sm text-[#1D1D1F] whitespace-pre-wrap">{selectedSubmission.company_info.analysis}</p>
                    </div>
                  )}
                </div>
              )}
              
              <div className="space-y-4">
                {((!showOriginal && selectedSubmission.conversation_history_de) || selectedSubmission.conversation_history).map((item, index) => (
                  <div key={index} className="bg-[#F5F5F7] rounded-xl p-4">
                    <p className="text-xs text-[#86868B] mb-2">Frage {index + 1}</p>
                    <p className="text-[#1D1D1F] font-medium mb-3">{item.question}</p>
                    <p className="text-xs text-[#0071E3] mb-1">Antwort</p>
                    <p className="text-[#1D1D1F]">{item.answer}</p>
                  </div>
                ))}
              </div>
              
              <Button
                onClick={() => setSelectedSubmission(null)}
                className="w-full mt-8 bg-[#0071E3] hover:bg-[#0077ED] text-white rounded-full h-12"
              >
                Schliessen
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
